<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'ageGroups' }"
          >Age Groups</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="my-6">
        <v-form ref="form" class="age-group-form">
          <label class="text-field-label">Name</label>
          <v-text-field
            flat
            solo
            required
            class="mt-2"
            v-model="form.age_group_name"
            :error-messages="form.$getError('age_group_name')"
            :loading="loading"
          ></v-text-field>

          <div class="d-flex">
            <div class="mr-3">
              <label class="text-field-label">Minimum Age</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                type="number"
                v-model="form.min_age"
                :error-messages="form.$getError('min_age')"
                :loading="loading"
              ></v-text-field>
            </div>

            <div>
              <label class="text-field-label">Maximum Age</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                type="number"
                v-model="form.max_age"
                :error-messages="form.$getError('max_age')"
                :loading="loading"
              ></v-text-field>
            </div>
          </div>

          <label class="text-field-label">Age Disclaimer?</label>
          <v-select
            class="mt-2"
            flat
            solo
            item-text="label"
            item-value="value"
            v-model="form.is_disclaimer"
            :items="[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' }
            ]"
            :error-messages="form.$getError('is_disclaimer')"
            :loading="loading"
          />

          <div class="d-flex mt-4">
            <v-btn
              v-if="form.id"
              color="error"
              class="mr-4 px-6"
              height="40px"
              :loading="form.$busy"
              @click="deleteDialog = true"
              >Delete</v-btn
            >

            <v-spacer></v-spacer>

            <v-btn
              color="primary"
              class="px-6"
              height="40px"
              @click="ageGroup ? updateAgeGroupDetails() : addAgeGroup()"
              :loading="form.$busy"
              >Save</v-btn
            >
          </div>
        </v-form>

        <v-dialog v-model="deleteDialog" max-width="290">
          <v-card>
            <v-card-title class="headline">Confirm Delete</v-card-title>

            <v-card-text>Are you sure you want to delete this?</v-card-text>

            <v-card-actions>
              <v-btn text @click="deleteDialog = false" :loading="form.$busy"
                >Cancel</v-btn
              >

              <v-spacer></v-spacer>

              <v-btn
                color="red"
                text
                @click="removeAgeGroup"
                :loading="form.$busy"
                >Yes, Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mdiChevronLeft, mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'

import Form from '@/utils/form'

export default {
  name: 'AgeGroupDetails',

  components: {
    AppBarNavIcon
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline
      },
      loading: false,
      deleteDialog: false,
      form: new Form({
        age_group_name: '',
        min_age: '',
        max_age: '',
        is_disclaimer: ''
      }),
      snackbar: {
        show: false,
        message: null,
        color: ''
      },
      showModal: false
    }
  },

  computed: {
    ...mapState({
      ageGroup: state => state.ageGroup.ageGroupDetails,
      authUser: state => state.auth.user
    }),
    displayName() {
      return this.form.age_group_name
    }
  },

  methods: {
    ...mapActions({
      getAgeGroupDetails: 'ageGroup/getAgeGroupDetails',
      createAgeGroup: 'ageGroup/createAgeGroup',
      updateAgeGroup: 'ageGroup/updateAgeGroup',
      deleteAgeGroup: 'ageGroup/deleteAgeGroup'
    }),

    ...mapMutations({
      clearAgeGroupDetails: 'ageGroup/clearAgeGroupDetails'
    }),

    async getAgeGroup() {
      this.loading = true
      await this.getAgeGroupDetails(this.$route.params.id)
      this.form = new Form(this.ageGroup)
      this.loading = false
    },

    async updateAgeGroupDetails() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.updateAgeGroup(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar(
            'Age group details successfully updated!',
            'success'
          )
          this.$router.replace({ name: 'ageGroups' })
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    async addAgeGroup() {
      this.form.$busy = true
      this.form.$clearErrors()
      this.createAgeGroup(this.form.$data())
        .then(() => {
          this.form.$busy = false
          this.showSnackbar('Age group successfully added!', 'success')
          this.$router.replace({ name: 'ageGroups' })
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
    },

    removeAgeGroup() {
      this.form.$busy = true
      this.deleteAgeGroup(this.form.id)
        .then(() => {
          this.form.$busy = false
          this.deleteDialog = false
          this.showSnackbar('Age group deleted successfully!')
          this.$router.replace('/age-groups')
        })
        .catch(err => {
          this.form.$busy = false
          if (err.response.status === 404) {
            this.deleteDialog = false
            this.showSnackbar(
              'The age group you are trying to delete is no longer existing.',
              'error'
            )
          }
        })
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    }
  },

  created() {
    if (this.$router.currentRoute.name === 'ageGroup.details') {
      this.getAgeGroup()
    }
  },

  destroyed() {
    this.clearAgeGroupDetails()
  },

  watch: {
    $route() {
      this.getAgeGroup()
    }
  }
}
</script>

<style lang="scss" scoped>
.age-group-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
